import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import { deleteObj } from "../core/delete";
import { findEntityByHandle, openAs } from "../core/oda";
import { OpenMode } from "../core/constants";
import { recordGraphicsModified } from "../entities";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";

export interface SetBlockAttributeParams {
  /** Handle of the block reference — the entity shown in the activity feed. */
  blockHandle: string;
  /** Handle of the OdDbAttribute actually modified. */
  attrHandle: string;
  /** Attribute tag, kept for the activity feed label. */
  tag: string;
  oldValue: string;
  newValue: string;
}

/**
 * Undo/redo for a block attribute text change, on any block reference.
 *
 * SetPhotoMarkerAttributeCommand stays as-is for photo markers (its oplog kind feeds
 * the activity feed differently); this one is its generic counterpart.
 *
 * The mutation is already applied by blocks/attributes.ts before the command is built.
 */
export class SetBlockAttributeCommand implements ICommand {
  private oplogEvents: OplogEmit[] = [];

  constructor(
    private readonly viewer: DRDrawingViewer,
    private readonly params: SetBlockAttributeParams,
  ) {}

  execute(): void {
    // Emitted on the block, not the attribute: the attribute never shows up as its
    // own row in the live scan (see parsing/activity.ts).
    this.oplogEvents = oplogEmitter.emitModified(this.viewer, [
      {
        handle: this.params.blockHandle,
        changeKind: "blockAttribute",
        oldValue: `${this.params.tag}=${this.params.oldValue}`,
        newValue: `${this.params.tag}=${this.params.newValue}`,
      },
    ]);
  }

  undo(): void {
    this.apply(this.params.oldValue);
    this.viewer.update();
    oplogEmitter.revert(this.oplogEvents);
  }

  redo(): void {
    this.apply(this.params.newValue);
    this.viewer.update();
    oplogEmitter.reemit(this.oplogEvents);
  }

  dispose(): void {}

  private apply(value: string): void {
    const { module, database } = this.viewer;
    const attrId = findEntityByHandle(module, database, this.params.attrHandle);
    if (!attrId || attrId.isNull()) return;

    const attr = openAs(attrId, module.OdDbAttribute, OpenMode.kForWrite);
    if (attr) {
      const odValue = new module.OdString(value);
      attr.setTextString(odValue);
      recordGraphicsModified(module, attrId);
      deleteObj(odValue);
      deleteObj(attr);
    }
    deleteObj(attrId);
  }
}
